import axios from 'axios';
import Swal from 'sweetalert2';
import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
// api
import ApiKey from '../api/ApiKey';
import BaseApi from '../api/BaseApi';
// components
import Sidebar from './Sidebar';
import NavBar from '../element/NavBar';
import Footer from '../element/Footer';
// helpers
import { logOutIfTokenExpired, resultInfo } from '../helpers';
// mui
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';

const EditManager = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('global');
  const userType = Cookies.get('user_type');
  const tokenKey = Cookies.get('tokenClient');

  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState({
    contact: '',
    position: '',
    last_name: '',
    first_name: '',
    role: 'manager',
    email_address: '',
  });

  const [errors, setErrors] = useState({
    contact: '',
    last_name: '',
    first_name: '',
    email_address: '',
  });

  const handleChange = e => {
    const { name, value } = e.target;

    setProfileData(prev => ({
      ...prev,
      [name]: value,
    }));

    setErrors(prev => ({
      ...prev,
      [name]: '',
    }));
  };

  const handleClick = async () => {
    try {
      const newErrors = {};

      if (!profileData.first_name) {
        newErrors.first_name = t('employerEditManager.firstNameRequired');
        window.scrollTo(0, 0);
      }
      if (!profileData.last_name) {
        newErrors.last_name = t('employerEditManager.lastNameRequired');
        window.scrollTo(0, 0);
      }
      if (!profileData.email_address) {
        newErrors.email_address = t('employerEditManager.emailRequired');
        window.scrollTo(0, 0);
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileData.email_address)) {
        newErrors.email_address = 'Неправильний формат електронної пошти';
        window.scrollTo(0, 0);
      }
      if (!profileData.contact) {
        newErrors.contact = t('employerEditManager.contactRequired');
        window.scrollTo(0, 0);
      } else if (!/^\+?3?8?(0\d{9})$/.test(profileData.contact)) {
        newErrors.contact = 'Номер має бути у форматі: +380XXXXXXXXX or 0XXXXXXXXX';
        window.scrollTo(0, 0);
      }

      setErrors(newErrors);

      if (Object.keys(newErrors).length === 0) {
        const confirmationResult = await Swal.fire({
          icon: 'question',
          showCancelButton: true,
          cancelButtonText: t('employerEditProfile.no'),
          confirmButtonText: t('employerEditProfile.yes'),
          title: id ? t('employerEditManager.editConfirmTitle') : t('employerEditManager.addConfirmTitle'),
        });

        if (confirmationResult.isConfirmed) {
          setLoading(true);

          const response = await axios.post(
            id
              ? BaseApi + `/company/editManager/${id}`
              : BaseApi + '/company/addManager',
            profileData,
            {
              headers: {
                key: ApiKey,
                token: tokenKey,
                contentType: 'application/json',
              },
            },
          );

          if (response.data.status === 200) {
            resultInfo(
              'success',
              id ? t('employerEditManager.editSuccessTitle') : t('employerEditManager.addSuccessTitle'),
              t('employerEditProfile.close'),
            );

            navigate('/company/managers');
          } else {
            Swal.fire({
              icon: 'error',
              text: response.data.message,
              title: t('employerEditManager.failedTitle'),
              confirmButtonText: t('employerCreateJob.close'),
            });
          }

          setLoading(false);
        }
      }
    } catch (error) {
      setLoading(false);

      if (error.message === 'Network Error' || error.response.data.message === 'Token has expired') {
        navigate('/');

        logOutIfTokenExpired(t('tokenExpired.tokenExpired'), t('tokenExpired.close'));
      } else {
        resultInfo('error', t('employerEditManager.failedTitle'), t('employerEditProfile.close'));
      }

      console.error('Could not submit edit data!', error);
    }
  };

  useEffect(() => {
    if (userType === 'candidate' || !tokenKey) {
      navigate('/auth/employer-login');
    } else {
      const fetchData = async () => {
        try {
          setLoading(true);

          const response = await axios.post(
            BaseApi + `/company/editManager/${id}`,
            null,
            {
              headers: {
                key: ApiKey,
                token: tokenKey,
                contentType: 'application/json',
              },
            },
          );

          setLoading(false);

          if (response.data.status === 200) {
            setProfileData(prev => ({ ...prev, ...response.data.response }));
          } else {
            resultInfo('error', response.data.message, t('employerCreateJob.close'));
          }
        } catch (error) {
          setLoading(false);

          if (error.message === 'Network Error' || error.response.data.message === 'Token has expired') {
            navigate('/');

            logOutIfTokenExpired(t('tokenExpired.tokenExpired'), t('tokenExpired.close'));
          }

          console.error('User details not received!', error);
        }
      };

      if (id) {
        fetchData();
      } else {
        setLoading(false);
      }

      window.scrollTo(0, 0);
    }
  }, [t, tokenKey, navigate, userType]); // eslint-disable-line

  return (
    <>
      <NavBar />
      <div className='container editProfile'>
        <div className='row'>
          <div className='col-lg-3'>
            <Sidebar />
          </div>
          {
            loading
              ? (
                <div className='loader-container'></div>
              ) : (
                <>
                  <div className='col-lg-9 mb-5 CLPanelRight'>
                    <div className='mb-5 d-flex PageHeader'>
                      {
                        id
                          ? <img alt='' src='/Images/employerSide/icon8color.png' />
                          : <PersonAddAltIcon style={{ color: '#4cb4b4', fontSize: '34px' }} />
                      }
                      <h3 className='mx-2'>
                        {id ? t('employerEditManager.editProfileInfo') : t('employerEditManager.addProfileInfo')}
                      </h3>
                    </div>
                    <form className=''>
                      <div className='form-outline mb-4 DashBoardInputBx'>
                        <label id='first_name' className='form-label' htmlFor='form3Example4'>
                          {t('employerEditManager.firstName')}
                          <span className='RedStar'>*</span>
                        </label>
                        <input
                          type='text'
                          name='first_name'
                          onChange={handleChange}
                          value={profileData.first_name}
                          placeholder={t('employerEditManager.firstNamePlaceholder')}
                          className={`form-control ${errors.first_name && 'input-error'}`}
                        />
                        {
                          errors.first_name && (
                            <div className='text-danger'>{errors.first_name}</div>
                          )
                        }
                      </div>
                      <div className='form-outline mb-4 DashBoardInputBx'>
                        <label id='last_name' className='form-label' htmlFor='form3Example5'>
                          {t('employerEditManager.lastName')}
                          <span className='RedStar'>*</span>
                        </label>
                        <input
                          type='text'
                          name='last_name'
                          onChange={handleChange}
                          value={profileData.last_name}
                          placeholder={t('employerEditManager.lastNamePlaceholder')}
                          className={`form-control ${errors.last_name && 'input-error'}`}
                        />
                        {
                          errors.last_name && (
                            <div className='text-danger'>{errors.last_name}</div>
                          )
                        }
                      </div>
                      <div className='form-outline mb-4 DashBoardInputBx'>
                        <label id='position' className='form-label' htmlFor='form3Example6'>
                          {t('employerEditManager.position')}
                        </label>
                        <input
                          type='text'
                          name='position'
                          onChange={handleChange}
                          value={profileData.position}
                          placeholder={t('employerEditManager.positionPlaceholder')}
                          className={`form-control ${errors.position && 'input-error'}`}
                        />
                        {
                          errors.position && (
                            <div className='text-danger'>{errors.position}</div>
                          )
                        }
                      </div>
                      <div className='form-outline mb-4 DashBoardInputBx'>
                        <label id='email_address' className='form-label' htmlFor='form3Example6'>
                          {t('employerEditManager.email')}
                          <span className='RedStar'>*</span>
                        </label>
                        <input
                          type='text'
                          disabled={id}
                          name='email_address'
                          onChange={handleChange}
                          value={profileData.email_address}
                          placeholder={t('employerEditManager.emailPlaceholder')}
                          className={`form-control ${errors.email_address && 'input-error'}`}
                        />
                        {
                          errors.email_address && (
                            <div className='text-danger'>{errors.email_address}</div>
                          )
                        }
                      </div>
                      <div className='form-outline mb-5 DashBoardInputBx'>
                        <label id='contact' className='form-label' htmlFor='form3Example8'>
                          {t('employerEditManager.contact')}
                          <span className='RedStar'>*</span>
                        </label>
                        <input
                          type='text'
                          name='contact'
                          onChange={handleChange}
                          value={profileData.contact}
                          placeholder={t('employerEditManager.contactPlaceholder')}
                          className={`form-control ${errors.contact && 'input-error'}`}
                        />
                        {
                          errors.contact && (
                            <div className='text-danger'>{errors.contact}</div>
                          )
                        }
                      </div>
                      <div className='bottomButtons'>
                        <button
                          type='button'
                          onClick={handleClick}
                          className='btn btn-primary button1'
                        >
                          {
                            id
                              ? t('employerEditProfile.updateButton')
                              : t('employerEditManager.addButton')
                          }
                        </button>
                        <button
                          type='button'
                          className='btn btn-primary button2'
                          onClick={() => window.history.back()}
                        >
                          {t('employerEditProfile.cancelButton')}
                        </button>
                      </div>
                    </form>
                  </div>
                </>
              )
          }
        </div>
      </div>
      <Footer />
    </>
  );
};

export default EditManager;
